import React, { useState, useEffect } from 'react';
import { getAnalysis } from '../api/crm';
import './AnalysisModal.css';

const AnalysisModal = ({ isOpen, onClose, period }) => {
    const [activeTab, setActiveTab] = useState('page');
    // Initialize with prop, but manage locally for modal-specific filtering
    const [localPeriod, setLocalPeriod] = useState(period || '1w');
    const [viewMode, setViewMode] = useState('count'); // 'count' or 'percent'
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

    const [expandedCategories, setExpandedCategories] = useState({}); // For Field Analysis Accordion

    // Sync with prop when modal opens or prop changes
    useEffect(() => {
        if (isOpen && period) {
            setLocalPeriod(period);
        }
    }, [isOpen, period]);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen, activeTab, localPeriod]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const result = await getAnalysis(activeTab, localPeriod);
            setData(result);
            setSortConfig({ key: null, direction: 'desc' }); // Reset sort on fetch
            setExpandedCategories({}); // Reset expansion
        } catch (error) {
            console.error('Failed to fetch analysis:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });

        const sortedData = [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setData(sortedData);
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="sort-icon">↕</span>;
        return <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="analysis-overlay">
            <div className="modal-content analysis-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <h3>📊 퍼포먼스 상세 분석</h3>
                        <div className="period-filters">
                            {['TD', '1w', '1m', '3m', '6m', '1y'].map(p => (
                                <button
                                    key={p}
                                    className={`modal-filter-btn ${localPeriod === p ? 'active' : ''}`}
                                    onClick={() => setLocalPeriod(p)}
                                >
                                    {p.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <div className="view-mode-toggle">
                            <button
                                className={`mode-btn ${viewMode === 'count' ? 'active' : ''}`}
                                onClick={() => setViewMode('count')}
                            >
                                횟수
                            </button>
                            <button
                                className={`mode-btn ${viewMode === 'percent' ? 'active' : ''}`}
                                onClick={() => setViewMode('percent')}
                            >
                                비율(%)
                            </button>
                        </div>
                    </div>
                    <button className="close-btn-icon" onClick={onClose}>&times;</button>
                </div>

                <div className="tabs">
                    <button className={`tab-btn ${activeTab === 'page' ? 'active' : ''}`} onClick={() => setActiveTab('page')}>페이지별 전환 분석</button>
                    <button className={`tab-btn ${activeTab === 'user' ? 'active' : ''}`} onClick={() => setActiveTab('user')}>방문자 활동 분석</button>
                    <button className={`tab-btn ${activeTab === 'field' ? 'active' : ''}`} onClick={() => setActiveTab('field')}>관심 분야 분석</button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="loading-spinner">데이터 분석 중...</div>
                    ) : (
                        renderContent(activeTab, data, handleSort, getSortIndicator, viewMode, expandedCategories, toggleCategory)

                    )}
                </div>
            </div>
        </div>
    );
};

const renderContent = (tab, data, handleSort, getSortIndicator, viewMode, expandedCategories, toggleCategory) => {
    if (!data || data.length === 0) return <div className="no-data">데이터가 없습니다.</div>;



    // Helper for Percentage
    const getTotalClicks = (dataset) => dataset.reduce((acc, item) => acc + (item.clicks || item.totalClicks || 0), 0);
    const total = getTotalClicks(data);

    const formatValue = (val) => {
        if (val === undefined || val === null) return '0';
        if (viewMode === 'percent') {
            return total > 0 ? ((val / total) * 100).toFixed(1) + '%' : '0%';
        }
        return val.toLocaleString(); // Add commas
    };

    if (tab === 'page') {
        const totalClicks = data.reduce((acc, item) => acc + (item.clicks || 0), 0);
        const avgClicks = data.length > 0 ? (totalClicks / data.length).toFixed(1) : '0';
        const totalSuccess = data.reduce((acc, item) => acc + (item.success || 0), 0);
        const totalFail = data.reduce((acc, item) => acc + (item.error || 0), 0);

        return (
            <div className="user-analysis-container">
                <div className="summary-cards">
                    <div className="summary-card">
                        <span className="summary-label">총 클릭 수</span>
                        <span className="summary-value">{totalClicks.toLocaleString()}</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">평균 클릭 수</span>
                        <span className="summary-value">{avgClicks}</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">총 성공</span>
                        <span className="summary-value success">{totalSuccess.toLocaleString()}</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">총 오류</span>
                        <span className="summary-value error">{totalFail.toLocaleString()}</span>
                    </div>
                </div>
                <table className="analysis-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('page')} style={{ cursor: 'pointer', width: '30%' }}>페이지 {getSortIndicator('page')}</th>
                            <th onClick={() => handleSort('action')} style={{ cursor: 'pointer', width: '20%' }}>전환 액션 {getSortIndicator('action')}</th>
                            <th onClick={() => handleSort('clicks')} style={{ cursor: 'pointer', width: '15%' }}>
                                {viewMode === 'percent' ? '클릭 비율' : '클릭 수'} {getSortIndicator('clicks')}
                            </th>
                            <th onClick={() => handleSort('success')} style={{ cursor: 'pointer', width: '15%' }}>성공 ✅ {getSortIndicator('success')}</th>
                            <th onClick={() => handleSort('error')} style={{ cursor: 'pointer', width: '20%' }}>오류 🚨 {getSortIndicator('error')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, idx) => (
                            <tr key={idx}>
                                <td className="bold">{item.page}</td>
                                <td>{item.action}</td>
                                <td className="text-right bold">{formatValue(item.clicks)}</td>
                                <td className="text-right success">{item.success}</td>
                                <td className="text-right error">{item.error}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (tab === 'user') {
        const totalVisitors = data.length;
        const totalPV = data.reduce((acc, item) => acc + (item.pv || 0), 0);
        const totalInquiries = data.reduce((acc, item) => acc + (item.inquiries || 0), 0);
        const totalClicks = data.reduce((acc, item) => acc + (item.clicks || 0), 0);

        return (
            <div className="user-analysis-container">
                <div className="summary-cards">
                    <div className="summary-card">
                        <span className="summary-label">총 방문자</span>
                        <span className="summary-value">{totalVisitors.toLocaleString()}명</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">총 페이지뷰 (PV)</span>
                        <span className="summary-value">{totalPV.toLocaleString()}</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">총 문의 시도</span>
                        <span className="summary-value">{totalInquiries.toLocaleString()}</span>
                    </div>
                    <div className="summary-card">
                        <span className="summary-label">총 클릭 수</span>
                        <span className="summary-value">{totalClicks.toLocaleString()}</span>
                    </div>
                </div>

                <table className="analysis-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('visitorId')} style={{ cursor: 'pointer', width: '25%' }}>
                                방문자 ID (익명) {getSortIndicator('visitorId')}
                            </th>
                            <th onClick={() => handleSort('lastActive')} style={{ cursor: 'pointer', width: '30%' }}>
                                최근 활동 시간 {getSortIndicator('lastActive')}
                            </th>
                            <th onClick={() => handleSort('pv')} style={{ cursor: 'pointer', width: '15%' }}>
                                {viewMode === 'percent' ? 'PV 비중' : '총 페이지뷰 (PV)'} {getSortIndicator('pv')}
                            </th>
                            <th onClick={() => handleSort('inquiries')} style={{ cursor: 'pointer', width: '15%' }}>
                                {viewMode === 'percent' ? '문의 비중' : '문의 시도'} {getSortIndicator('inquiries')}
                            </th>
                            <th onClick={() => handleSort('clicks')} style={{ cursor: 'pointer', width: '15%' }}>
                                {viewMode === 'percent' ? '클릭 비중' : '총 클릭 수'} {getSortIndicator('clicks')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, idx) => {
                            const showPercent = viewMode === 'percent';
                            const pvDisplay = showPercent ? (totalPV > 0 ? ((item.pv / totalPV) * 100).toFixed(1) + '%' : '0%') : item.pv?.toLocaleString();
                            const inqDisplay = showPercent ? (totalInquiries > 0 ? ((item.inquiries / totalInquiries) * 100).toFixed(1) + '%' : '0%') : item.inquiries?.toLocaleString();
                            const clickDisplay = showPercent ? (totalClicks > 0 ? ((item.clicks / totalClicks) * 100).toFixed(1) + '%' : '0%') : item.clicks?.toLocaleString();

                            return (
                                <tr key={idx}>
                                    <td className="mono">{item.visitorId}</td>
                                    <td>{new Date(item.lastActive).toLocaleString()}</td>
                                    <td className="text-right">{pvDisplay}</td>
                                    <td className="text-right bold">{inqDisplay}</td>
                                    <td className="text-right">{clickDisplay}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    if (tab === 'field') {
        // Hierarchical Display for Field Analysis
        // Structure: { category, totalClicks, cases: [{name, clicks}, ...] }
        return (
            <div className="field-analysis">
                <div className="field-header">
                    <span>관심 분야 (카테고리)</span>
                    <span>{viewMode === 'percent' ? '비율' : '총 클릭 수'}</span>
                </div>
                {data.map((item, idx) => {
                    const isExpanded = expandedCategories[item.category];
                    const percent = total > 0 ? (item.totalClicks / total) * 100 : 0;

                    return (
                        <div key={idx} className="field-acc-item">
                            <div className="field-acc-header" onClick={() => toggleCategory(item.category)}>
                                <div className="field-info">
                                    <span className="field-name">
                                        {isExpanded ? '▼' : '▶'} {item.category}
                                    </span>
                                    <div className="field-stats">
                                        <span className="field-count bold">{formatValue(item.totalClicks)}</span>
                                        <div className="progress-bar-mini">
                                            <div className="progress-fill" style={{ width: `${percent}%` }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="field-acc-body">
                                    {item.cases.length > 0 ? (
                                        <table className="sub-case-table">
                                            <thead>
                                                <tr>
                                                    <th>Case 명</th>
                                                    <th className="text-right">클릭 수</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {item.cases.map((c, cIdx) => (
                                                    <tr key={cIdx}>
                                                        <td>{c.name}</td>
                                                        <td className="text-right">
                                                            {/* Calculate sub-percent relative to Category Total or Global Total? 
                                                                Usually relative to Category makes sense inside, but ViewMode implies global comparison.
                                                                Let's follow viewMode logic: if percent, show global % or local %?
                                                                "누르면 비율기준으로 나오는거" -> Usually global share.
                                                                But inside a category, maybe local share is better?
                                                                I'll use global share for consistency with the toggle.
                                                             */}
                                                            {viewMode === 'percent' ?
                                                                (total > 0 ? ((c.clicks / total) * 100).toFixed(1) + '%' : '0%') :
                                                                c.clicks
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="no-sub-data">세부 데이터 없음</div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
};

export default AnalysisModal;
